/**
 * PathSelector Component
 * Allows users to choose between Blueprint (prompts) or Deploy (Cloudflare) paths
 * Shows Deploy option to everyone but requires sign-in to select it
 */

import React from 'react';
import { Icon } from './ui/index.jsx';

const paths = [
    {
        id: 'blueprint',
        title: 'Architect',
        subtitle: 'Generate Architecture',
        description: 'Get production-ready prompts, diagrams, and tech stack to build with any AI tool',
        icon: 'document',
        features: ['200+ specific prompts', 'Architecture diagrams', 'Tech stack recommendations'],
        badge: null,
        requiresAuth: true
    },
    {
        id: 'deploy',
        title: 'Deploy',
        subtitle: 'Build & Launch',
        description: 'We build and deploy your app directly to your Cloudflare infrastructure',
        icon: 'lightning',
        features: ['Full working app', 'Your own infrastructure', 'Database & storage included'],
        badge: null,
        requiresAuth: true
    },
    {
        id: 'vibe',
        title: 'Vibe Coding',
        subtitle: 'Code in Real-Time',
        description: 'Chat with AI and watch your app come to life instantly with live preview',
        icon: 'code',
        features: ['Real-time preview', 'Iterative building', 'Deploy when ready'],
        badge: 'New',
        requiresAuth: true
    }
];

export default function PathSelector({ value, onChange, disabled = false, isAuthenticated = false }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {paths.map((path) => {
                const isSelected = value === path.id;
                const requiresAuth = path.requiresAuth && !isAuthenticated;
                const isDeployPath = path.id === 'deploy';
                const isVibePath = path.id === 'vibe';

                // Color scheme based on path type
                const accentColor = isVibePath ? '#06B6D4' : isDeployPath ? '#F6821F' : '#7B61FF';
                const accentColorLight = isVibePath ? '#22D3EE' : isDeployPath ? '#F6821F' : '#BA87FF';

                return (
                    <button
                        key={path.id}
                        type="button"
                        onClick={() => !disabled && !requiresAuth && onChange(path.id)}
                        disabled={disabled || requiresAuth}
                        className={`
                            relative text-left p-6 rounded-2xl border-2 transition-all duration-300
                            ${isSelected
                                ? isVibePath
                                    ? 'border-[#06B6D4] bg-[#06B6D4]/10 shadow-[0_0_30px_rgba(6,182,212,0.2)]'
                                    : isDeployPath
                                        ? 'border-[#F6821F] bg-[#F6821F]/10 shadow-[0_0_30px_rgba(246,130,31,0.2)]'
                                        : 'border-[#7B61FF] bg-[#7B61FF]/10 shadow-[0_0_30px_rgba(123,97,255,0.2)]'
                                : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
                            }
                            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                            ${requiresAuth ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                    >
                        {/* Badge */}
                        {requiresAuth ? (
                            <span className="absolute top-4 right-4 px-2 py-0.5 text-xs font-semibold bg-gray-600 text-gray-300 rounded-full flex items-center gap-1">
                                <Icon name="lock" size="xs" />
                                Sign in required
                            </span>
                        ) : path.badge && (
                            <span className={`absolute top-4 right-4 px-2 py-0.5 text-xs font-semibold text-white rounded-full ${
                                isVibePath
                                    ? 'bg-gradient-to-r from-[#06B6D4] to-[#22D3EE]'
                                    : isDeployPath
                                        ? 'bg-gradient-to-r from-[#F6821F] to-[#FAAD3F]'
                                        : 'bg-gradient-to-r from-[#7B61FF] to-[#BA87FF]'
                            }`}>
                                {path.badge}
                            </span>
                        )}

                        {/* Icon */}
                        <div className={`
                            w-14 h-14 rounded-xl flex items-center justify-center mb-4
                            ${isSelected
                                ? isVibePath ? 'bg-[#06B6D4]/20' : isDeployPath ? 'bg-[#F6821F]/20' : 'bg-[#7B61FF]/20'
                                : 'bg-white/10'
                            }
                        `}>
                            <Icon
                                name={path.icon}
                                size="xl"
                                className={isSelected
                                    ? isVibePath ? 'text-[#06B6D4]' : isDeployPath ? 'text-[#F6821F]' : 'text-[#7B61FF]'
                                    : 'text-gray-400'
                                }
                            />
                        </div>

                        {/* Title & Subtitle */}
                        <div className="mb-2">
                            <h3 className={`text-xl font-bold ${isSelected ? 'text-white' : 'text-gray-200'}`}>
                                {path.title}
                            </h3>
                            <span className={`text-sm ${isSelected
                                ? isVibePath ? 'text-[#06B6D4]' : isDeployPath ? 'text-[#F6821F]' : 'text-[#7B61FF]'
                                : 'text-gray-500'
                            }`}>
                                {path.subtitle}
                            </span>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-gray-400 mb-4">
                            {path.description}
                        </p>

                        {/* Features */}
                        <ul className="space-y-2">
                            {path.features.map((feature, idx) => (
                                <li key={idx} className="flex items-center gap-2 text-sm">
                                    <Icon
                                        name="check"
                                        size="xs"
                                        className={isSelected
                                            ? isVibePath ? 'text-[#06B6D4]' : isDeployPath ? 'text-[#F6821F]' : 'text-[#7B61FF]'
                                            : 'text-gray-500'
                                        }
                                    />
                                    <span className="text-gray-300">{feature}</span>
                                </li>
                            ))}
                        </ul>

                        {/* Selection Indicator */}
                        <div className={`
                            absolute bottom-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center
                            transition-all duration-300
                            ${isSelected
                                ? isVibePath
                                    ? 'border-[#06B6D4] bg-[#06B6D4]'
                                    : isDeployPath
                                        ? 'border-[#F6821F] bg-[#F6821F]'
                                        : 'border-[#7B61FF] bg-[#7B61FF]'
                                : 'border-gray-600 bg-transparent'
                            }
                        `}>
                            {isSelected && (
                                <Icon name="check" size="xs" className="text-white" />
                            )}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}
